import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import { getSession, SESSION_QUERY_KEY } from '@/features/sessions/api'
import type { GetSessionResponseType } from '@/features/sessions/types'
import { SessionStatusEnumType, type V2SessionType } from '@/features/sessions/types'
import { setAdminSessionStatus } from '@/features/sessions/utils'
import checkPermissionsGroup from '@/features/users/utils'
import { DEFAULT_QUERY_STALE_TIME } from '@/lib/vue-query'
function V1ToV2DataMiddleware({
  data,
  userId,
}: {
  data: GetSessionResponseType | undefined
  userId: string | undefined
}): V2SessionType | null {
  const session = data?.session

  if (!session) return null

  const users = session.mentorshipGroup.groupUsers
    // get ONLY the mentor and mentees
    .filter((groupUser) => checkPermissionsGroup(groupUser.roles, PermissionsGroupsEnumType.USER))
    .map((groupUser) => ({
      id: groupUser.user.id,
      roles: groupUser.roles,
      firstName: groupUser.user.identity.firstname,
      lastName: groupUser.user.identity.lastname,
      email: groupUser.user.email,
      picture: groupUser.user.personalInformation?.profilPicture ?? null,
      sessionStatus:
        session.statuses?.find((status) => status.userId === groupUser.user.id)?.status ?? SessionStatusEnumType.NONE,
    }))

  let sessionStatus: SessionStatusEnumType
  const isUserInSession = users.some((user) => user.id === userId)

  // if the user is in the session, we get his status
  if (isUserInSession) {
    sessionStatus = session.statuses.find((status) => status.userId === userId)?.status || SessionStatusEnumType.NONE
    // else if it's admin, we get the status of the session
  } else {
    sessionStatus = setAdminSessionStatus(session)
  }

  return {
    id: session.id,
    mentoringGroupId: session.mentoringGroupId,
    createdBy: session.createdBy ?? null,
    mentoringGroupName: session.mentorshipGroup.customName ?? session.mentorshipGroup.name,
    meetingRoomId: session.mentorshipGroup.meetingRoomId,
    at: session.from,
    from: session.from,
    to: session.to,
    name: session.name,
    location: session.location,
    duration: session.duration,
    status: sessionStatus,
    report: session.report ?? null,
    menteeReports: session.menteeReports ?? [],
    users,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    reviews: session.reviews,
    presences: session.presences.map((presence) => ({
      userId: presence.userId,
      wasPresent: presence.isPresent,
    })),
    deliverables: session.deliverables,
    objective: session.objectives?.[0] ?? null,
  } satisfies V2SessionType
}

const useSession = (
  sessionIdRef: Ref<string | null>,
  { keepQueryAlive }: { keepQueryAlive: boolean } = { keepQueryAlive: false },
) => {
  const { accountRef } = useAccount()

  // Check if all the required data is available to make the query
  const isSessionQueryEnabledRef = computed(() => !!sessionIdRef.value)

  const sessionQuery = useQuery({
    queryKey: [
      SESSION_QUERY_KEY,
      {
        id: sessionIdRef,
      },
    ],
    queryFn: () =>
      getSession({
        id: sessionIdRef.value!,
      }),
    enabled: () => isSessionQueryEnabledRef.value,
    staleTime: keepQueryAlive ? Infinity : DEFAULT_QUERY_STALE_TIME,
  })

  const data = computed(() => {
    return V1ToV2DataMiddleware({
      data: sessionQuery.data.value,
      userId: accountRef.value?.id,
    })
  })

  return {
    sessionRef: data,
    isPendingRef: sessionQuery.isPending,
  }
}

export default useSession
